import type { Book, Me, SettingsPatch } from '@wellread/api';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { call, client, keys, queryClient } from './api';
import { dueAcross } from './books';

// ---- Reads ----

export const meQuery = { queryKey: keys.me, queryFn: () => call(client.GET('/v1/me')) };
export const useMe = () => useQuery(meQuery);

const working = new Set<Book['status']>(['uploading', 'parsing', 'extracting', 'verifying', 'linking']);

// Live events drive progress; this slow poll is the fallback if the stream
// drops or an event is missed while a book is building.
const whileBuilding = (books: Book[] | undefined) =>
  books?.some((b) => working.has(b.status)) ? 4000 : false;

export const booksQuery = { queryKey: keys.books, queryFn: () => call(client.GET('/v1/books')) };
export const useBooks = () =>
  useQuery({ ...booksQuery, refetchInterval: (q) => whileBuilding(q.state.data?.books) });

/**
 * What a review across every book would ask now, from the shelf: the top
 * bar's Review says it on every page. It never polls; the shelf's own pages do.
 */
export const useDueEverywhere = () => useQuery({ ...booksQuery, select: (d) => dueAcross(d.books) });

export function bookQuery(id: string) {
  return {
    queryKey: keys.book(id),
    queryFn: () => call(client.GET('/v1/books/{id}', { params: { path: { id } } })),
    // Seed from the shelf so the book page renders instantly.
    initialData: () =>
      queryClient.getQueryData<{ books: Book[] }>(keys.books)?.books.find((b) => b.id === id),
    initialDataUpdatedAt: () => queryClient.getQueryState(keys.books)?.dataUpdatedAt,
  };
}
export const useBook = (id: string) =>
  useQuery({
    ...bookQuery(id),
    refetchInterval: (q) => whileBuilding(q.state.data ? [q.state.data] : undefined),
  });

/** The reader's tracks, in shelf order, each with its books and how it is going. */
export const tracksQuery = { queryKey: keys.tracks, queryFn: () => call(client.GET('/v1/tracks')) };
export const useTracks = (enabled = true) => useQuery({ ...tracksQuery, enabled });

/** The top level of a track's map: its themes, each with how much of it the reader holds. */
export const useTrackMap = (track: string, enabled = true) =>
  useQuery({
    queryKey: keys.map('user', undefined, undefined, undefined, track),
    queryFn: () => call(client.GET('/v1/map', { params: { query: { scope: 'user', track_id: track } } })),
    enabled,
  });

export const useContents = (id: string, enabled = true) =>
  useQuery({
    queryKey: keys.contents(id),
    queryFn: () => call(client.GET('/v1/books/{id}/contents', { params: { path: { id } } })),
    enabled,
  });

export function marksQuery(id: string, idx: number) {
  return {
    queryKey: keys.marks(id, idx),
    queryFn: () =>
      call(client.GET('/v1/books/{id}/marks', { params: { path: { id }, query: { chapter: idx } } })),
  };
}
export const useMarks = (id: string, idx: number) =>
  useQuery({ ...marksQuery(id, idx), placeholderData: keepPreviousData });

export const useAnnotations = (id: string) =>
  useQuery({
    queryKey: keys.annotations(id),
    queryFn: () => call(client.GET('/v1/books/{id}/annotations', { params: { path: { id } } })),
  });

export const useCast = (id: string) =>
  useQuery({
    queryKey: keys.cast(id),
    queryFn: () => call(client.GET('/v1/books/{id}/cast', { params: { path: { id } } })),
  });

export const useStory = (id: string) =>
  useQuery({
    queryKey: keys.story(id),
    queryFn: () => call(client.GET('/v1/books/{id}/story', { params: { path: { id } } })),
  });

/** Pages, time and concepts mastered. Asked again on every visit: a stretch of reading or a session may have just ended. */
export const useReadingStats = () =>
  useQuery({
    queryKey: keys.stats,
    queryFn: () => call(client.GET('/v1/reading/stats')),
    refetchOnMount: 'always',
  });

export const useNode = (id: string | undefined) =>
  useQuery({
    queryKey: keys.node(id ?? ''),
    queryFn: () => call(client.GET('/v1/nodes/{id}', { params: { path: { id: id! } } })),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

export const useDossier = (bookId: string, nodeId: string | undefined) =>
  useQuery({
    queryKey: keys.dossier(bookId, nodeId ?? ''),
    queryFn: () =>
      call(client.GET('/v1/books/{id}/cast/{node}', { params: { path: { id: bookId, node: nodeId! } } })),
    enabled: !!nodeId,
    placeholderData: keepPreviousData,
  });

export const creditsQuery = {
  queryKey: keys.credits,
  queryFn: () => call(client.GET('/v1/me/credits')),
};
export const useCredits = () => useQuery(creditsQuery);

/**
 * Where the reader stands and what a month costs. Asking also finds a
 * payment the reader made that never got reported, so it is asked afresh
 * each time a screen that shows it opens.
 */
export const billingQuery = {
  queryKey: keys.billing,
  queryFn: () => call(client.GET('/v1/billing')),
  staleTime: 0,
};
export const useBilling = (enabled = true) => useQuery({ ...billingQuery, enabled });

/** The balance and what changed it, newest first. */
export type Credits = Awaited<ReturnType<typeof creditsQuery.queryFn>>;
export type CreditEvent = Credits['events'][number];

// A live event refreshes Explore when a round of suggestions lands; the
// slow poll covers a dropped stream while one is being made.
export const useExplore = (enabled = true) =>
  useQuery({
    queryKey: keys.explore,
    queryFn: () => call(client.GET('/v1/explore')),
    enabled,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 6000 : false),
  });

/**
 * What Explore remembers about the reader, newest first, and the authors
 * they follow, by name. Asked again on every visit: any conversation may
 * have added to it.
 */
export const useRemembered = () =>
  useQuery({
    queryKey: keys.remembered,
    queryFn: () => call(client.GET('/v1/me/remembered')),
    refetchOnMount: 'always',
  });

// ---- Writes ----

/**
 * Settings change immediately on screen; the server merges per field.
 * queue: patches that share a queue's name are sent one at a time, in order.
 */
export function usePatchSettings(queue?: string) {
  return useMutation({
    scope: queue ? { id: queue } : undefined,
    mutationFn: (patch: Omit<SettingsPatch, 'updated_at'>) =>
      call(client.PATCH('/v1/me/settings', { body: { ...patch, updated_at: new Date().toISOString() } })),
    onMutate: async (patch) => {
      // The write comes first, before any await: a controlled input reads the
      // cache, and a microtask's gap would flick it back for a frame.
      const prev = queryClient.getQueryData<Me>(keys.me);
      if (prev) {
        const s = prev.settings;
        queryClient.setQueryData<Me>(keys.me, {
          ...prev,
          settings: {
            profile: { ...s.profile, ...(patch.profile as object) },
            reader: { ...s.reader, ...(patch.reader as object) },
            session: { ...s.session, ...(patch.session as object) },
            chrome: { ...s.chrome, ...(patch.chrome as object) },
          },
        });
      }
      await queryClient.cancelQueries({ queryKey: keys.me });
      return { prev };
    },
    onError: (_e, _p, ctx) => ctx?.prev && queryClient.setQueryData(keys.me, ctx.prev),
    onSuccess: (me) => queryClient.setQueryData(keys.me, me),
  });
}

/**
 * Adds a book: registers it, uploads the file straight to storage with
 * progress, then starts the parse. Nothing is sent to a model yet.
 */
export async function uploadBook(file: File, onProgress: (fraction: number) => void): Promise<Book> {
  const up = await call(client.POST('/v1/books', { body: { filename: file.name } }));
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', up.upload_url);
    xhr.setRequestHeader(
      'Content-Type',
      /\.pdf$/i.test(file.name) ? 'application/pdf' : 'application/epub+zip',
    );
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () =>
      xhr.status < 300 ? resolve() : reject(new Error(xhr.responseText || `Upload stopped (${xhr.status}).`));
    xhr.onerror = () => reject(new Error('The upload lost its connection. Try again.'));
    xhr.send(file);
  });
  const book = await call(client.POST('/v1/books/{id}/ingest', { params: { path: { id: up.book.id } } }));
  refreshBook(book);
  return book;
}

export function refreshBook(b: Book) {
  queryClient.setQueryData(keys.book(b.id), b);
  void queryClient.invalidateQueries({ queryKey: keys.books });
  // A book's build, its place and its reviews all show in its track's figures.
  void queryClient.invalidateQueries({ queryKey: keys.tracks });
}

/** No event carries the balance: ask for it again after anything that uses a credit or gives one back. */
export function refreshCredits() {
  void queryClient.invalidateQueries({ queryKey: keys.me });
  void queryClient.invalidateQueries({ queryKey: keys.credits });
}
